"use client";

import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "./utils";
import { useReadOnlyFields } from "./read-only-fields";

function Switch({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  const readOnlyFields = useReadOnlyFields();
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      disabled={disabled || readOnlyFields}
      className={cn(
        // The track is 18x32px — well under a thumb. Growing the control
        // itself is not an option: the thumb slides by
        // `translate-x-[calc(100%-2px)]`, calibrated to this exact width. A
        // transparent `::before` hit-tests as part of its originating element,
        // so a tap anywhere in it flips the switch. It grows the target to
        // 44px tall and stays exactly as wide as the track — widening it too
        // would let it overhang a neighbouring control and steal its taps,
        // and would add to an ancestor scroll container's scrollWidth. It
        // paints nothing, and is dropped from `sm:` up where a pointer is precise.
        "peer relative data-[state=checked]:bg-primary data-[state=unchecked]:bg-switch-background focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80 inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 before:absolute before:top-1/2 before:left-0 before:h-11 before:w-full before:-translate-y-1/2 before:content-[''] sm:before:hidden",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          "bg-card dark:data-[state=unchecked]:bg-card-foreground dark:data-[state=checked]:bg-primary-foreground pointer-events-none block size-4 rounded-full ring-0 transition-transform data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0",
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
